// Typed contracts for the hubx-item-domain v2 API (HUBX-8385).
// Shapes mirror the API exactly (design doc 2615050241 §4.1/§8.3; lookups per HUBX-8378).
// Kept colocated with the item-create feature — this is the v2 boundary, deliberately
// separate from the legacy interfaces under vendor/interface/.

/** GET /lookups/manufacturers */
export interface ILookupManufacturer {
  id: string;
  name: string;
}

/** GET /lookups/categories — a tree; leaf nodes have no children. */
export interface ILookupCategory {
  categoryId: string;
  name: string;
  children?: ILookupCategory[];
}

export interface ILookupAttributeValue {
  code: string;
  value: string;
}

/** GET /lookups/attributes — e.g. Condition / Packaging / Warranty. */
export interface ILookupAttribute {
  id: string;
  label: string;
  values: ILookupAttributeValue[];
}

/** GET /lookups/exw-points */
export interface ILookupExwPoint {
  id: number;
  description: string;
}

/** Attribute selection sent on create: the lookup attribute id + the selected value CODE
 *  (the domain persists AttributeId + ValueCode — not the display value). */
export interface ICreateItemAttribute {
  attributeId: string;
  value?: string;
}

/** Quantity-break price pair (DQ #1–3). Client rule: fromQty >= 2 (qty 1 is the base price). */
export interface ICreateItemDiscountQty {
  fromQty: number;
  price: number;
}

/**
 * POST /items body — mirror of CreateItemRequest (hubx.item.api).
 * Optional members must be OMITTED when not applicable, never sent as null:
 * the edge treats a present-but-empty SpecialNotes/Restrictions as a 400 (rule 5),
 * and absent HubxItemCode/MinimumOrderQty take server-side defaults.
 * Status axes are never sent (ADR-0009 rule 5); createApproved is the only
 * status-related input and only ADMIN/VM may assert it.
 */
export interface ICreateItemRequest {
  vendorItemCode: string;
  mpn: string;
  description: string;
  manufacturerId: string;
  categoryId: string;
  hubxItemCode?: string;
  /**
   * The company to create for (ADR-0010). The client sends its raw selection; the
   * server decides whether it is a real retarget against the caller's authoritative
   * context and collapses a value equal to that context to "no retarget". Absent
   * (SELLER, or the operator's selection never resolved) means the ambient vendor.
   * Operator roles only: a SELLER supplying a DIFFERING company is rejected with
   * TARGET_VENDOR_NOT_ALLOWED, never ignored.
   */
  targetVendorId?: string;
  unitPrice?: number;
  unitPurchasePrice?: number;
  availability: number;
  minimumOrderQty?: number;
  maximumOrderQty?: number;
  leadTimeDays: number;
  exwId: number;
  createApproved?: boolean;
  specialNotes?: string;
  restrictions?: string;
  attributes: ICreateItemAttribute[];
  discountQtys?: ICreateItemDiscountQty[];
  tags?: string[];
}

/**
 * GET /items/{id} response — the canonical item resource (backend ItemResponse,
 * HUBX-8389 / design doc §8.3). Carries every Create input with ids already
 * resolved (no client-side name matching) so the Create modal can be seeded for
 * Clone (§4.3). Clone uses a SUBSET: it ignores the status axes, effectiveStatus
 * and the rowVersion tokens — those exist for Edit (HUBX-8367). attributes,
 * discountQtys and tags reuse the Create shapes; tags surfaced here are
 * vendor-editable only (IsManual = 0). JSON is camelCase; rowVersion tokens are
 * base64 strings.
 */
export interface ItemResponse {
  id: string;
  itemId: string;
  vendorItemCode: string;
  hubxItemCode?: string | null;
  mpn?: string | null;
  description?: string | null;
  manufacturerId?: string | null;
  categoryId?: string | null;
  unitPrice?: number | null;
  unitPurchasePrice?: number | null;
  availability: number;
  minimumOrderQty: number;
  maximumOrderQty?: number | null;
  leadTimeDays?: number | null;
  exwId: number;
  specialNotes?: string | null;
  restrictions?: string | null;
  attributes: ICreateItemAttribute[];
  discountQtys: ICreateItemDiscountQty[];
  tags: string[];
  systemStatus: string;
  vendorStatus: string;
  businessStatus: string;
  effectiveStatus: string;
  rowVersion: string;
  itemRowVersion: string;
}

/**
 * A non-blocking advisory on a SUCCESSFUL write (backend WriteWarning, design doc
 * §8.1). Never alters the 201/200; `code` is the stable contract and `data` is a
 * flat bag whose keys are part of the contract for that code. New advisories add a
 * code, so consumers must match on `code` and ignore what they don't know.
 */
export interface IWriteWarning {
  code: string;
  data?: { [key: string]: string };
}

/** The `code` the per-vendor category divergence is published under (HUBX-8428);
 *  its `data.categoryId` is the category the shared item is filed under. */
export const CATEGORY_DIFFERS_FROM_MASTER = 'CATEGORY_DIFFERS_FROM_MASTER';

/** POST /items 201 body (backend ItemCreateResponse). Only `warnings` is consumed
 *  today — the id comes from the Location header — but the shape is typed in full
 *  so a future reader is not tempted to re-derive it. */
export interface IItemCreateResponse {
  id: string;
  itemId: string;
  itemWasCreated: boolean;
  vendorItemCode: string;
  hubxItemCode: string;
  rowVersion: string;
  itemRowVersion?: string | null;
  warnings: IWriteWarning[];
  commandId: string;
}

/** Outcome of a successful create: 201 + Location: /items/{id}, plus the write-ack
 *  advisories (empty, never null — HUBX-8428). */
export interface ICreateItemResult {
  id: string;
  location: string;
  warnings: IWriteWarning[];
}

/**
 * GET /items/resolutions?mpn={mpn} — whether an MPN already resolves to an existing
 * item (HUBX-8430), feeding the pre-submit category nudge (HUBX-8431).
 *
 * `categoryId` is that item's category and is nullable EVEN WHEN `resolved` is true:
 * a legacy item may carry none, and then there is nothing to compare against. The
 * server always answers 200 — `resolved: false` means the MPN would create a new
 * item, which is an answer, not an error.
 */
export interface IItemResolution {
  resolved: boolean;
  itemId?: string | null;
  categoryId?: string | null;
}

/**
 * What the Clone header says about the item the modal was seeded FROM
 * (HUBX-8422). Assembled from the list row, never from ItemResponse: the read
 * representation is the edit contract and carries no activity dates and no
 * seller — and must not grow them just to feed a header.
 *
 * `id` is what the parent actually fetches with; the rest is display only, and
 * every display member is optional because the row supplies them conditionally
 * (a Seller sees no company name; an item that never went live has no launch
 * date). Absent members are omitted from the strip rather than rendered blank.
 */
export interface ICloneSource {
  id: string;
  vendorItemCode: string;
  companyName?: string | null;
  launchedOn?: string | Date | null;
  updatedOn?: string | Date | null;
}

/** RFC 9457 ProblemDetails as emitted by the v2 API; `code` is the stable
 *  extension member the UI routes errors by. `errors` carries per-member
 *  validation failures on 400 (ASP.NET ValidationProblemDetails shape). */
export interface IItemProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  errors?: { [member: string]: string[] };
}
